/// <amd-module name="Feed/Core/Redux/FeedHub/FeedHubActions" />
import { Action, ActionsUnion, createAction, trackedAction, trackedErrorAction } from "redux/Utils/ActionHelper";

import { HubActionStrings } from "Feed/Common/Constants/Constants";
import { Feed, FeedPermission, FeedView, Package } from "Feed/Common/Contracts/Feed";
import { FeedMessage, UpstreamHealthStatus } from "Feed/Common/Contracts/WebPage";
import { IDependency } from "Feed/Common/Types/IDependency";
import { IError } from "Feed/Common/Types/IError";
import { IPackageVersion } from "Feed/Core/Types";

import { hubSwitchAction } from "../Navigation/NavigationActions";
import { FeedDialogData, FeedHubData } from "./FeedHubContracts";

// Exported for use with reducer case statements
export const enum FeedHubActionTypes {
    // User Actions
    ShowGlobalSettings = "GlobalSettings/Show",
    HideGlobalSettings = "GlobalSettings/HidePanel",

    ShowCreateFeed = "CreateProjectScopedFeed/Show",
    HideCreateFeed = "CreateProjectScopedFeed/Hide",

    ShowIvyPromote = "ShowIvyPromote/Show",
    HideIvyPromote = "ShowIvyPromote/Hide",

    ViewFeed = "Feed/ViewFeed",
    FeedSelected = "Feed/FeedSelected",
    FeedDeleted = "Feed/FeedDeleted",
    FeedRestored = "Feed/FeedRestored",
    FeedPermanentDeleted = "Feed/PermanentDeleted",
    FeedUpdated = "Feed/Updated",
    ShowFeedSettings = "FeedSettings/Show",
    ShowRecycleBin = "RecycleBin/Show",

    PackageSelected = "Packages/Selected",
    PackageDependencySelected = "Packages/DependencySelected",
    RecycleBinPackageSelected = "RecycleBin/Selected",
    ConnectToFeed = "Feed/ConnectToFeed",

    // API & Data Actions
    InitialDataLoaded = "FeedHub/InitialDataLoaded",
    InitialDataLoadFailed = "FeedHub/InitialDataLoadFailed",
    FeedAdded = "CreateProjectScopedFeed/Add",

    LoadFeedMessageStarted = "FeedHub/LoadFeedMessageStarted",
    SetUpstreamStatuses = "UI/FeedHub/SetUpstreamStatuses",

    GetFeedCompleted = "#api/Feed/GetFeedCompleted",
    GetFeedPermissionsCompleted = "#api/Feed/GetFeedPermissionsCompleted",
    GetFeedViewsCompleted = "#api/Feed/GetFeedViewsCompleted",

    // UI Actions
    SetErrorMessage = "UI/FeedHub/SetErrorMessage",
    SetFeedMessages = "UI/FeedHub/SetFeedMessages",
    DeleteFeedMessage = "UI/FeedHub/DeleteFeedMessage",
    CreateDialog = "UI/FeedHub/CreateDialog",
    DialogLoading = "UI/FeedHub/DialogLoading",
    DismissDialog = "UI/FeedHub/DismissDialog"
}

// Created actions
export const FeedHubActions = {
    // User Actions
    showGlobalSettings: () => trackedAction(createAction(FeedHubActionTypes.ShowGlobalSettings)),
    hideGlobalSettings: () => createAction(FeedHubActionTypes.HideGlobalSettings),

    showCreateFeed: () => trackedAction(createAction(FeedHubActionTypes.ShowCreateFeed)),
    hideCreateFeed: () => createAction(FeedHubActionTypes.HideCreateFeed),

    showIvyPromote: (packageVersion: IPackageVersion) =>
        trackedAction(createAction(FeedHubActionTypes.ShowIvyPromote, packageVersion)),
    hideIvyPromote: () => createAction(FeedHubActionTypes.HideIvyPromote),

    feedSelected: (feedId: string) => createAction(FeedHubActionTypes.FeedSelected, feedId),
    viewFeed: () => feedPageChangeAction(createAction(FeedHubActionTypes.ViewFeed), HubActionStrings.ViewFeed),
    feedDeleted: (feedId: string) => createAction(FeedHubActionTypes.FeedDeleted, feedId),
    feedRestored: (feedId: string) => createAction(FeedHubActionTypes.FeedRestored, feedId),
    feedPermanentDeleted: (feedId: string) => createAction(FeedHubActionTypes.FeedPermanentDeleted, feedId),
    feedUpdated: (feed: Feed) => createAction(FeedHubActionTypes.FeedUpdated, feed),
    showFeedSettings: () =>
        feedPageChangeAction(createAction(FeedHubActionTypes.ShowFeedSettings), HubActionStrings.Settings, {
            useParentFeedView: true
        }),
    showRecycleBin: () =>
        feedPageChangeAction(createAction(FeedHubActionTypes.ShowRecycleBin), HubActionStrings.RecycleBin, {
            useParentFeedView: true
        }),

    packageSelected: (packageSummary: Package) =>
        hubSwitchAction(createAction(FeedHubActionTypes.PackageSelected, packageSummary), HubActionStrings.ViewPackage),
    packageDependencySelected: (dependency: IDependency, originPackage: Package) =>
        hubSwitchAction(
            createAction(FeedHubActionTypes.PackageDependencySelected, { dependency, originPackage }),
            HubActionStrings.PackageDependencySelected
        ),
    recycleBinPackageSelected: (packageSummary: Package) =>
        hubSwitchAction(
            createAction(FeedHubActionTypes.RecycleBinPackageSelected, packageSummary),
            HubActionStrings.RecycleBinPackage
        ),
    connectToFeed: () => feedPageChangeAction(createAction(FeedHubActionTypes.ConnectToFeed), HubActionStrings.Connect),

    // Api Actions
    initialDataLoaded: (data: FeedHubData) => createAction(FeedHubActionTypes.InitialDataLoaded, data),
    initialDataLoadFailed: (error: Error) =>
        trackedErrorAction(createAction(FeedHubActionTypes.InitialDataLoadFailed, error)),
    feedAdded: (feed: Feed) => createAction(FeedHubActionTypes.FeedAdded, feed),

    loadFeedMessageStarted: () => createAction(FeedHubActionTypes.LoadFeedMessageStarted),
    setUpstreamStatuses: (feedId: string, upstreamStatuses: UpstreamHealthStatus[] | undefined) =>
        createAction(FeedHubActionTypes.SetUpstreamStatuses, { feedId, upstreamStatuses }),

    getFeedCompleted: (feed: Feed) => createAction(FeedHubActionTypes.GetFeedCompleted, feed),
    getFeedPermissionsCompleted: (feedId: string, feedPermissions: FeedPermission[]) =>
        createAction(FeedHubActionTypes.GetFeedPermissionsCompleted, { feedId, feedPermissions }),
    getFeedViewsCompleted: (feedId: string, feedViews: FeedView[]) =>
        createAction(FeedHubActionTypes.GetFeedViewsCompleted, { feedId, feedViews }),

    // UI Actions
    setErrorMessage: (error: IError | undefined) =>
        trackedErrorAction(createAction(FeedHubActionTypes.SetErrorMessage, error)),
    clearErrorMessage: () => createAction(FeedHubActionTypes.SetErrorMessage, undefined),
    setFeedMessages: (feedId: string, feedMessages: FeedMessage[] | undefined) =>
        createAction(FeedHubActionTypes.SetFeedMessages, { feedId, feedMessages }),
    deleteFeedMessage: (feedId: string, messageIndex: number) =>
        createAction(FeedHubActionTypes.DeleteFeedMessage, { feedId, messageIndex }),
    createDialog: (dialogData: FeedDialogData) => createAction(FeedHubActionTypes.CreateDialog, dialogData),
    dialogLoading: () => createAction(FeedHubActionTypes.DialogLoading),
    dismissDialog: () => createAction(FeedHubActionTypes.DismissDialog)
};

export const enum FeedHubMetaTypes {
    FEED_HUBACTION = "FEED_HUBACTION",
    FEED_HUBACTIONOPTIONS = "FEED_HUBACTIONOPTIONS"
}

export interface IFeedHubActionOptions {
    useParentFeedView: boolean;
}

// Use this action to trigger a page change using the currently selected feed
export function feedPageChangeAction<T, A extends Action<T>>(
    action: A,
    hubAction: string,
    options?: IFeedHubActionOptions
): A {
    if (!action.meta) {
        action.meta = {};
    }

    action.meta[FeedHubMetaTypes.FEED_HUBACTION] = hubAction;
    if (options != null) {
        action.meta[FeedHubMetaTypes.FEED_HUBACTIONOPTIONS] = options;
    }

    return action;
}

export type FeedHubActions = ActionsUnion<typeof FeedHubActions>;
